import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import ProgressOverviewCards from './components/ProgressOverviewCards';
import AchievementShowcase from './components/AchievementShowcase';
import SkillBreakdown from './components/SkillBreakdown';
import RecentActivity from './components/RecentActivity';

const StudentProgressCenter = () => {
  const { user, userProfile } = useAuth();
  const [progressData, setProgressData] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchProgressData();
    }
  }, [user?.id]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user progress data with play information
      const { data: progressData, error: progressError } = await supabase?.from('user_progress')?.select(`
          *,
          plays (
            id,
            title,
            total_lines
          )
        `)?.eq('user_id', user?.id)?.order('last_accessed_at', { ascending: false });

      if (progressError) throw progressError;

      // Fetch all achievements
      const { data: achievementsData, error: achievementsError } = await supabase?.from('achievements')?.select('*')?.order('xp_reward', { ascending: false });

      if (achievementsError) throw achievementsError;

      // Fetch user's earned achievements
      const { data: userAchievementsData, error: userAchievementsError } = await supabase?.from('user_achievements')?.select(`
          *,
          achievements (*)
        `)?.eq('user_id', user?.id)?.order('earned_at', { ascending: false });

      if (userAchievementsError) throw userAchievementsError;

      // Fetch quiz attempts
      const { data: quizAttemptsData, error: quizAttemptsError } = await supabase?.from('quiz_attempts')?.select('*')?.eq('user_id', user?.id)?.order('completed_at', { ascending: false })?.limit(10);

      if (quizAttemptsError) throw quizAttemptsError;

      setProgressData({
        progress: progressData || [],
        quizAttempts: quizAttemptsData || []
      });
      setAchievements(achievementsData || []);
      setUserAchievements(userAchievementsData || []);
    } catch (err) {
      console.error('Error fetching progress data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate overall stats
  const calculateStats = () => {
    if (!progressData || !userProfile) return null;

    const totalXP = userProfile?.xp_points || 0;
    const currentLevel = userProfile?.level || 1;
    const playsStarted = progressData?.progress?.length || 0;
    const playsCompleted = progressData?.progress?.filter(p => p?.status === 'completed')?.length || 0;
    const totalLinesRead = progressData?.progress?.reduce((sum, p) => sum + (p?.lines_completed || 0), 0);
    
    // Calculate average quiz accuracy
    const quizScores = progressData?.quizAttempts?.map(q => q?.score || 0);
    const avgQuizAccuracy = quizScores?.length > 0 
      ? Math.round(quizScores?.reduce((a, b) => a + b, 0) / quizScores?.length) 
      : 0;

    // Calculate consecutive days (mock for now - would need actual login tracking)
    const consecutiveDays = 7;

    return {
      totalXP,
      currentLevel,
      playsStarted,
      playsCompleted,
      totalLinesRead,
      avgQuizAccuracy,
      consecutiveDays,
      earnedAchievements: userAchievements?.length || 0,
      totalAchievements: achievements?.length || 0
    };
  };

  const stats = calculateStats();

  return (
    <>
      <Helmet>
        <title>Progress Center - BardPlay</title>
        <meta 
          name="description" 
          content="Track your learning achievements, skill development, and engagement metrics with BardPlay's comprehensive progress dashboard" 
        />
      </Helmet>

      <RoleBasedNavigation />

      <main className="min-h-screen bg-gradient-to-br from-surface-dark via-[#1a0033] to-surface-dark pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="TrendingUp" size={40} className="text-accent-purple" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-accent-purple to-accent-blue bg-clip-text text-transparent">
                Your Progress Journey
              </h1>
            </div>
            <p className="text-text-secondary text-lg">
              Track your achievements, monitor your learning progress, and celebrate your accomplishments
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
              <Icon name="AlertCircle" size={24} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-red-400 font-semibold mb-1">Error Loading Progress Data</h3>
                <p className="text-red-300/80 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)]?.map((_, index) => (
                  <div key={index} className="bg-surface-lighter border border-border rounded-xl h-32 animate-pulse" />
                ))}
              </div>
              <div className="bg-surface-lighter border border-border rounded-xl h-64 animate-pulse" />
              <div className="bg-surface-lighter border border-border rounded-xl h-96 animate-pulse" />
            </div>
          )}

          {/* Progress Content */}
          {!loading && !error && stats && (
            <div className="space-y-8">
              {/* Overview Cards */}
              <ProgressOverviewCards stats={stats} />

              {/* Achievement Showcase */}
              <AchievementShowcase 
                achievements={achievements}
                userAchievements={userAchievements}
                totalXP={stats?.totalXP}
              />

              {/* Skill Breakdown */}
              <SkillBreakdown 
                progressData={progressData}
                stats={stats}
              />

              {/* Recent Activity */}
              <RecentActivity 
                progressData={progressData}
                userAchievements={userAchievements}
              />
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && (!progressData || progressData?.progress?.length === 0) && (
            <div className="text-center py-16">
              <Icon name="TrendingUp" size={64} className="text-text-secondary mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">Start Your Journey</h3>
              <p className="text-text-secondary mb-6">
                Begin exploring plays and completing quizzes to track your progress
              </p>
              <button
                onClick={() => window.location.href = '/student-plays-library'}
                className="btn-primary"
              >
                Browse Plays
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default StudentProgressCenter;